import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Play, Eye, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuthStore } from '@/app/store/authStore';
import { UserProfile } from '@/app/types/auth';
import { ReturnGenerationPreview, ReturnGenerationDashboard } from '@/app/types/compliance';
import {
  previewReturnGeneration,
  runReturnGeneration,
  getReturnGenerationDashboard,
} from '@/app/services/returnGenerationService';
import ReturnGenerationDashboardComponent from './components/ReturnGenerationDashboard';
import ReturnGenerationPreviewComponent from './components/ReturnGenerationPreview';

// Auth selectors
const selectUser = (state: { user: UserProfile | null }) => state.user;
const selectLogoutAction = (state: { logoutAction: () => void }) => state.logoutAction;
const selectIsLoading = (state: { isLoading: boolean }) => state.isLoading;

const ReturnGenerationPage: React.FC = () => {
  const queryClient = useQueryClient();

  // Local State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [targetDate, setTargetDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [previewData, setPreviewData] = useState<ReturnGenerationPreview | undefined>();
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);

  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);

  const userOrgId = useMemo(
    () => user?.organization_id || user?.organization?.id,
    [user]
  );
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);

  // Query for dashboard
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
  } = useQuery<ReturnGenerationDashboard>({
    queryKey: ['return-generation-dashboard', userOrgId],
    queryFn: () => getReturnGenerationDashboard(),
    enabled: !!userOrgId,
    refetchInterval: 60000, // Refresh every minute
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: () => previewReturnGeneration({ target_date: targetDate }),
    onSuccess: (data) => {
      setPreviewData(data);
      toast.success('Preview generated successfully');
    },
    onError: (error) => {
      toast.error(
        `Failed to generate preview: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  // Run mutation
  const runMutation = useMutation({
    mutationFn: () =>
      runReturnGeneration({ target_date: targetDate, dry_run: false }),
    onSuccess: (data) => {
      toast.success(`Successfully created ${data.returns_created} compliance tasks`);
      queryClient.invalidateQueries({ queryKey: ['return-generation-dashboard', userOrgId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setIsRunDialogOpen(false);
      setPreviewData(undefined);
    },
    onError: (error) => {
      toast.error(
        `Failed to run generation: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    },
  });

  const handlePreview = () => {
    previewMutation.mutate();
  };

  const handleRunClick = () => {
    if (!previewData || previewData.returns_created === 0) {
      toast.warning('Please preview first to see what tasks will be created');
      return;
    }
    setIsRunDialogOpen(true);
  };

  const handleRunConfirm = () => {
    runMutation.mutate();
  };

  // --- Render Logic ---

  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage return generation.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Return Generation</h1>
          <p className="text-muted-foreground">
            Automatically generate compliance tasks for upcoming filing deadlines.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="generate">Generate Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          <ReturnGenerationDashboardComponent
            data={dashboardData}
            isLoading={isDashboardLoading}
          />
        </TabsContent>

        <TabsContent value="generate" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Generate Compliance Tasks</CardTitle>
              <CardDescription>
                Preview and create compliance tasks for subscribed clients based on
                their return type configurations and due dates.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Selection */}
              <div className="flex items-end gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target-date">Target Date</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="target-date"
                      type="date"
                      value={targetDate}
                      onChange={(e) => {
                        setTargetDate(e.target.value);
                        setPreviewData(undefined);
                      }}
                      className="pl-10 w-[200px]"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tasks due on or before this date will be generated
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewMutation.isPending}
                  >
                    {previewMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    Preview
                  </Button>
                  {isOrgAdmin && (
                    <Button
                      onClick={handleRunClick}
                      disabled={
                        runMutation.isPending ||
                        !previewData ||
                        previewData.returns_created === 0
                      }
                    >
                      {runMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" />
                      )}
                      Generate Tasks
                    </Button>
                  )}
                </div>
              </div>

              {/* Preview Results */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-4">Preview Results</h3>
                <ReturnGenerationPreviewComponent
                  data={previewData}
                  isLoading={previewMutation.isPending}
                  isError={previewMutation.isError}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Run Confirmation Dialog */}
      <AlertDialog open={isRunDialogOpen} onOpenChange={setIsRunDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate Compliance Tasks</AlertDialogTitle>
            <AlertDialogDescription>
              This will create <strong>{previewData?.returns_created || 0}</strong>{' '}
              compliance tasks based on the preview. These tasks will be assigned to
              the configured users and will appear in the task list.
              <br />
              <br />
              Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRunConfirm}>
              {runMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Tasks'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReturnGenerationPage;
